<!-- eslint-disable vue/no-undef-properties -->
<template>
  <div v-if="object">
    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
      <span v-if="!isNewObjectType">
        {{ mappingType == "direct" ? $t("Direct") : $t("Cross") }} {{ $t("Mapping of") }} {{ integration_name }} {{ $t("object") }}
        <strong>{{ object.label }}</strong> {{ $t("to revsetter object") }}
        <strong class="capitalize">{{ $utils.ObjectType(mappedObjectType).label }}</strong> <br><br>
      </span>
      <span v-else>
        {{ $t("Mapping data of") }} {{ integration_name }} {{ $t("object") }} <strong>{{ object.name }} </strong>
        {{ $t("to") }}
        <strong class="capitalize">{{ $t("New revsetter object") }} {{ $utils.slugify(object.label.replace("__c", ""), "_") }}</strong>
        <br><br>
      </span>
    </p>
    <div class="mb-6 text-gray-600">
      {{
        $t(
          "In RevSetter we refer to fields or data points as Metrics. When you select a field/data point to map into RevSetter, we will convert it to what we call a “metric” and you will be able to use the data inside RevSetter. Metrics are dynamic and powerful in RevSetter - you can use them across the platform, not just to view the data, but also to use the data across many areas like filters, segments, triggers, and much more. You don't need to bring in all your data from this object. Instead, you can select the specific fields that you want to map for a better experience."
        )
      }}
    </div>
    <div class="flex flex-col w-full">
      <div class="w-full p-3 mx-auto lg:flex">
        <div class="relative w-full h-full max-w-full max-h-full bg-white">
          <div
            style="height: calc(100vh - 100px)"
            class="relative w-full max-w-full max-h-full overflow-x-auto overflow-y-auto shadow sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="z-20 w-full h-12 bg-gray-50" style="position: sticky; top: 0">
                <tr>
                  <th scope="col" class="flex py-1 font-bold text-left text-gray-700 text-md">
                    <span class="px-4 py-2"> {{ $t("Fields") }} </span>
                    <div class="relative w-2/3 rounded-md shadow-sm">
                      <input
                        ref="search_input"
                        v-model="metric_filter"
                        autocomplete="off"
                        :autofocus="true"
                        type="text"
                        name="search_default_metrics"
                        class="pl-10 form-input"
                        :placeholder="$t('Filter by field name')">
                      <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
                      </div>
                    </div>
                  </th>
                  <th scope="col" class="pl-20 text-sm font-bold text-left text-gray-500">
                    <div class="flex flex-row items-center justify-start gap-2 py-2 pl-2 truncate">
                      <input
                        id="push-everything"
                        v-model="toggle_all_is_checked"
                        name="push-notifications"
                        type="checkbox"
                        class="w-5 h-5 border-gray-400 rounded focus:ring-blue-light text-blue-light"
                        @click="toggleAllSources">
                      <label for="push-everything" class="block text-sm font-bold text-gray-500">
                        <div class="flex flex-row items-start justify-center text-gray-500 has-tooltip">
                          {{ $t("Create Metric") }} ?
                          <div class="top-auto flex-wrap p-2 mt-5 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                            {{ $t("Whether or not to create a metric from this source") }}
                          </div>
                        </div>
                      </label>
                    </div>
                  </th>
                  <th scope="col" class="text-sm font-bold text-left text-gray-500 w-15">
                    {{ $t("Metric name") }}
                  </th>
                  <th
                    v-for="field_mapping in current_object_header_fields_mappings"
                    :key="field_mapping.name"
                    scope="col"
                    class="relative px-6 py-3">
                    <div
                      class="flex flex-row justify-center font-medium text-gray-500 cursor-help has-tooltip first:underline hover:text-blue">
                      {{ field_mapping.label }}
                      <span v-if="field_mapping.is_required" class="text-red-400 text-md">*</span>
                    </div>
                  </th>
                  <th scope="col" class="relative px-6 py-3">
                    <div
                      class="flex flex-row justify-center font-medium text-gray-500 has-tooltip first:underline hover:text-blue dark:text-white">
                      {{ $t("Mapping") }}
                    </div>
                  </th>
                  <th v-if="mappingType === 'cross'" scope="col" class="relative px-6 py-3">
                    <div
                      class="flex flex-row justify-center font-medium text-gray-500 has-tooltip first:underline hover:text-blue dark:text-white">
                      {{ $t("Is Primary/Unique Key") }}
                      <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip">
                        {{
                          $t(
                            "Primary unique keys are used to identify a records uniquely in a your records list. A primary key cannot be NULL, and must have a mapped metric"
                          )
                        }}
                      </div>
                    </div>
                  </th>
                  <!-- <th scope="col" class="relative px-6 py-3">
                    <div
                      class="flex flex-row justify-center font-medium text-gray-500 has-tooltip first:underline hover:text-blue dark:text-white">
                      Partial match (Advanced)
                      <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip">
                        Partial match is used to match a record in your records list with a record in your
                        {{ $route.params.integration_name.capitalize() }} instance. This is useful when you have a record in your
                        {{ $route.params.integration_name.capitalize() }} instance that is a substring of a record in your records list and
                        vice versa
                      </div>
                    </div>
                  </th> -->
                </tr>
              </thead>
              <tbody>
                <template v-if="current_object.fields.length > 0">
                  <tr
                    v-for="(field, fieldIdx) in object_fields.filter(
                      ({ name }) => !metric_filter || (metric_filter && name.toLowerCase().search(metric_filter.toLowerCase()) != -1)
                    )"
                    :key="fieldIdx"
                    :class="fieldIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                    class="group hover:bg-blue hover:cursor-pointer hover:text-white"
                    @click.stop="
                      () => {
                        if (!field.is_checked && !field.edit_mode) {
                          setIsCheckedSourceStatus(field, current_object, !field.is_checked)
                        }
                      }
                    ">
                    <td class="px-5 py-2 text-sm font-medium text-gray-900 whitespace-pre">
                      <div class="flex-1 min-w-0 text-sm">
                        <label
                          :for="`metric-${current_object.name + field.name}`"
                          class="font-medium text-gray-700 select-none group-hover:text-white">{{ field.label }}</label>
                        <span class="ml-2 font-light text-gray-400 select-none text-xsm group-hover:text-white">[{{ field.data_type }}]</span>
                        <p class="font-light text-gray-400 select-none text-xsm group-hover:text-white">
                          [{{ field.is_relation ? field.reference_to[0] : field.input_type }}]
                        </p>
                      </div>
                    </td>
                    <td class="px-5 py-2 pl-20 text-sm text-gray-500 whitespace-nowrap">
                      <div class="flex items-center pl-2">
                        <input
                          :id="`metric-${current_object.name + field.name}`"
                          v-model="field.is_checked"
                          :value="field.is_checked"
                          :name="`metric-${current_object.name + field.name}`"
                          type="checkbox"
                          class="w-5 h-5 border-gray-300 rounded focus:ring-blue-light text-blue-light group-hover:checked:bg-blue-dark"
                          @click.stop="
                            () => {
                              setIsCheckedSourceStatus(field, current_object, !field.is_checked)
                            }
                          ">
                      </div>
                    </td>

                    <td class="text-sm text-gray-500 whitespace-nowrap overflow-ellipsis">
                      <div class="relative flex items-start">
                        <div class="min-w-0 text-sm">
                          <div v-if="field.edit_mode && field.is_checked" class="flex flex-row items-center justify-center w-full">
                            <input
                              v-model="field.metric_name"
                              type="text"
                              class="block border-gray-300 rounded-md shadow-sm focus:ring-blue focus:border-blue sm:text-sm"
                              placeholder="Metric name"
                              @keyup.enter="setEditSourceStatus(field, current_object, false)">
                            <Check
                              class="block w-10 h-10 p-2 text-gray-500 transition duration-500 ease-in-out hover:bg-gray-100"
                              aria-hidden="true"
                              @click.stop="
                                () => {
                                  if (field.metric_name.length > 0) {
                                    setEditSourceStatus(field, current_object, false)
                                  }
                                }
                              " />
                          </div>
                          <div v-else-if="field.is_checked && !field.edit_mode" class="h-full py-2">
                            <label
                              :for="`metric-${current_object.name + field.name}`"
                              :class="[field.is_checked ? 'opacity-100' : 'opacity-0']"
                              class="font-medium text-gray-400 select-none hover:cursor-text group-hover:text-white"
                              @click.stop="setEditSourceStatus(field, current_object, true)">{{ field.metric_name }}</label>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="field_mapping in current_object_header_fields_mappings"
                      :key="field_mapping.name + fieldIdx"
                      class="px-2 py-2 text-sm font-medium text-right whitespace-nowrap">
                      <div class="min-w-0 text-sm">
                        <div class="flex flex-row items-center justify-center w-full">
                          <div class="flex items-center justify-center w-10 h-10 has-tooltip">
                            <input
                              :name="!field_mapping.is_list ? field_mapping.name : field.name"
                              :type="!field_mapping.is_list ? 'radio' : 'checkbox'"
                              :disabled="isFieldDisabled(field_mapping, field)"
                              :checked="isFieldMapped(field_mapping.name, field)"
                              :class="{
                                'rounded-full': !field_mapping.is_list,
                                'rounded-md': field_mapping.is_list
                              }"
                              class="flex items-center justify-center w-6 h-6 border border-gray-400 cursor-pointer focus:ring-blue text-blue-light disabled:cursor-default disabled:opacity-30 group-hover:checked:bg-blue-dark"
                              @click.stop="radioBtnClicked(field_mapping, field, current_object)">

                            <div class="flex-wrap p-2 mb-16 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                              {{ field_mapping.label }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="px-2 py-2 text-sm font-medium text-right whitespace-nowrap">
                      <div class="min-w-full text-sm">
                        <div class="flex flex-row items-center justify-center w-full">
                          <div class="flex items-center justify-center">
                            <ComboBoxInput
                              :value="field.mapped_metric"
                              :disabled="field.is_disabled"
                              :items="field.available_options"
                              :show-label="true"
                              :allow-null="false"
                              :return-null="false"
                              :floating-label="true"
                              label-attr="label"
                              name="company_id_field"
                              value-attr="value"
                              :is-box-background="true"
                              :align-right="true"
                              label=""
                              classes="max-w-[200px] min-w-[200px]"
                              @update:current-item="
                                (item) => {
                                  field.mapped_metric = item
                                  object_fields[fieldIdx].mapped_metric = field.mapped_metric
                                  updateField(field, current_object)
                                }
                              " />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td v-if="mappingType === 'cross'" class="px-2 py-2 text-sm font-medium text-right whitespace-nowrap">
                      <div class="min-w-0 text-sm">
                        <div class="flex flex-row items-center justify-center w-full">
                          <div class="flex items-center justify-center w-10 h-10">
                            <input
                              :name="field.value"
                              :disabled="
                                !(
                                  field.mapped_metric != 'create' &&
                                  (field.data_type == 'String' || field.data_type == 'Number' || field.data_type == 'Json')
                                )
                              "
                              type="checkbox"
                              :checked="field.is_primary_key"
                              class="flex items-center justify-center w-6 h-6 border border-gray-400 rounded-md cursor-pointer focus:ring-blue text-blue-light disabled:cursor-default disabled:opacity-30 group-hover:checked:bg-blue-dark"
                              @click.stop="
                                () => {
                                  field.is_primary_key = !field.is_primary_key
                                  updateField(field, current_object)
                                }
                              ">
                          </div>
                        </div>
                      </div>
                    </td>
                    <!-- <td class="px-2 py-2 text-sm font-medium text-right whitespace-nowrap">
                      <div class="min-w-0 text-sm">
                        <div class="flex flex-col items-center justify-center w-full">
                          <div class="flex items-center justify-center w-10 h-10">
                            <ComboBoxInput
                              :value="field.partial_match"
                              :disabled="!field.is_primary_key"
                              :items="[
                                { label: '', value: 'no' },
                                {
                                  label: `Yes (Substring of field ${field.name} equals ${
                                    $store.state.metrics.find((m) => m.id == field.mapped_metric)?.name
                                  } metric)`,
                                  value: 'on_field'
                                },
                                {
                                  label: `Yes (Substring of metric ${
                                    $store.state.metrics.find((m) => m.id == field.mapped_metric)?.name
                                  } equals ${field.name} field)`,
                                  value: 'on_metric'
                                }
                              ]"
                              :show-label="true"
                              :allow-null="false"
                              :return-null="false"
                              :floating-label="true"
                              label-attr="label"
                              name="partial_match"
                              value-attr="value"
                              :is-box-background="true"
                              :align-right="true"
                              label=""
                              classes="max-w-[200px] min-w-[200px]"
                              @update:current-item="
                                (item) => {
                                  field['partial_match'] = item
                                  updateField(field, current_object)
                                }
                              " />
                          </div>
                          <div v-if="field.partial_match != 'no'" class="flex flex-row items-center justify-center w-full">
                            <input
                              v-model="field.partial_match_regex"
                              type="text"
                              class="block border-gray-300 rounded-md shadow-sm hover:text-gray-900 group-hover:text-gray-900 focus:ring-blue focus:border-blue sm:text-sm"
                              placeholder="RegEx"
                              @change="updateField(field, current_object)">
                          </div>
                        </div>
                      </div>
                    </td> -->
                  </tr>
                </template>

                <template v-else-if="current_object.fields.length == 0 && current_object.show_fields">
                  <tr class="group hover:bg-blue hover:cursor-pointer hover:text-white">
                    <td class="px-5 py-2 pl-20 text-sm font-medium text-gray-900 whitespace-pre">
                      {{ $t("No supported fields found") }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <CompanyFieldMapping
      v-model:is-open="is_company_field_mapping_modal_open"
      :mapped-object-type="mappedObjectType"
      @submitParentMapping="handleCompanyFieldMappingDone($event)"
      @cancel="is_company_field_mapping_modal_open = false" />

    <UsersInvite
      v-model:is-open="is_send_invitation_emails_modal_open"
      :service-users="service_found_users"
      @submitInvitedUsers="submitInvitedUsersAndCreateMetrics($event)" />

    <ObjectsFilter v-model:is-open="is_filter_records_modal_open" :objects="objects" @submitFilters="submitFilterAndStartSync($event)" />

    <Modal v-model:is-open="show_uploading_error">
      <template #form>
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-white">
          {{ $t("Please make the following actions") }}
          <TriangleAlert class="float-right w-6 h-6 text-blue-light" aria-hidden="true" />
        </h3>
        <p class="text-sm text-gray-500" />
        <div class="flex flex-col justify-between flex-1">
          <div class="divide-y divide-gray-200">
            <div class="pb-5">
              <div class="mt-2">
                <div class="mt-2">
                  <p v-for="(er, index) in Array.from(uploading_errors)" :key="er" class="text-sm text-gray-500">
                    <strong>{{ index + 1 }}. </strong> {{ er }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="sm:flex sm:flex-row-reverse">
          <button type="button" class="btn-primary" @click="show_uploading_error = false">{{ $t("OK") }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import Modal from "@/components/shared/Modal.vue"
import UsersInvite from "@/views/dashboard/admin/integrations/data/steps/mapping/UsersInvite.vue"
import CompanyFieldMapping from "@/views/dashboard/admin/integrations/data/steps/mapping/CompanyFieldMapping.vue"
import ObjectsFilter from "@/views/dashboard/admin/integrations/data/steps/mapping/ObjectsFilter.vue"
import { Search, Check, TriangleAlert } from "lucide-vue-next"
import IntegrationService from "@/services/integration.service"
import { utils } from "@/store/helpers"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters.js"
export default {
  name: "DataMapping",
  components: {
    CompanyFieldMapping,
    UsersInvite,
    ObjectsFilter,
    ComboBoxInput,
    TriangleAlert,
    Check,
    Modal,
    Search
  },
  props: {
    object: {
      type: Object,
      required: true
    },
    mappedObjectType: {
      type: String,
      default: null, // null for new custom objects
      required: true
    },
    isNewObjectType: {
      type: Boolean,
      default: false,
      required: false
    },
    mappingType: {
      type: String,
      required: true,
      default: "direct"
    },
    mapping: {
      type: Object,
      default: null,
      required: false
    }
  },
  emits: ["update:object"],
  data() {
    return {
      toggle_all_is_checked: false,
      metric_filter: null,
      show_uploading_error: false,
      current_object_opened_filter: null,
      object_fields: [],
      is_filter_records_modal_open: false,
      direct_objects_mapping: {},
      cross_objects_mapping: {},
      relationship_mapping: {},
      required_mapping: new Set(),
      uploading_errors: new Set(),
      objects: [],
      is_company_field_mapping_modal_open: false,
      company_field_mapping_current_field: null,
      service_found_users: [],
      current_object: {
        fields: null
      },
      selected_sources: [],
      is_send_invitation_emails_modal_open: false,
      options_mapper: {
        object_id_field: "id",
        object_name_field: "name",
        object_website_field: "website",
        object_industry_field: "industry",
        object_parent_id_field: "parent_id",
        object_owner_id_field: "owner_id",
        object_co_owners_ids_fields: null,
        object_company_id_field: "company_id",
        object_first_name_field: "first_name",
        object_last_name_field: "last_name",
        object_email_field: "email"
      },
      list_of_platform_metrics_names: [
        "company_name_metric",
        "company_website_metric",
        "contact_email_metric",
        "contact_first_name_metric",
        "contact_last_name_metric",
        "opportunity_amount_metric",
        "opportunity_name_metric"
      ],
      list_of_options: [
        "object_id_field",
        "object_name_field",
        "object_website_field",
        "object_parent_id_field",
        "object_owner_id_field",
        "object_co_owners_ids_fields",
        "object_company_id_field",
        "object_first_name_field",
        "object_last_name_field",
        "object_email_field"
      ],
      standard_object_types: ["company", "contact", "opportunity", "user"],
      cross_mapping_fields: [
        {
          name: "object_id_field",
          label: this.$t("Id"),
          allowed_data_types: ["string", "number", "json"],
          in_header: true,
          is_required: true
        },
        {
          name: "object_name_field",
          label: this.$t("Name"),
          allowed_data_types: ["string", "double", "float", "number", "character varying", "character", "int"],
          is_required: this.mappingType === "direct",
          in_header: this.mappingType === "direct"
        }
      ],
      standard_object_types_mapping_fields: {
        company: [
          {
            name: "object_id_field",
            label: this.$t("Id"),
            allowed_data_types: ["string", "number"],
            in_header: true,
            is_required: true
          },
          {
            name: "object_name_field",
            label: this.$t("Name"),
            allowed_data_types: ["string", "number"],
            in_header: this.mappingType === "direct",
            is_required: this.mappingType === "direct"
          },
          {
            name: "object_website_field",
            label: this.$t("Website/Domain"),
            allowed_data_types: ["string"],
            is_required: false,
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_parent_id_field",
            label: this.$t("Parent Id"),
            allowed_data_types: ["json", "string", "number"],
            is_required: false,
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_owner_id_field",
            label: this.$t("Owner Id"),
            allowed_data_types: ["json"],
            is_required: false,
            in_header: this.mappingType === "direct" && this.$route.params.integration_name != "spreadsheet"
          },
          {
            name: "object_co_owners_ids_fields",
            label: this.$t("Co Owners Ids"),
            is_list: true,
            allowed_data_types: ["json"],
            is_required: false,
            in_header: this.mappingType === "direct" && this.$route.params.integration_name != "spreadsheet"
          }
        ],
        opportunity: [
          {
            name: "object_id_field",
            label: this.$t("Id"),
            allowed_data_types: ["string", "number"],
            in_header: true,
            is_required: true
          },
          {
            name: "object_name_field",
            label: this.$t("Name"),
            allowed_data_types: ["string", "number"],
            in_header: true,
            is_required: this.mappingType === "direct"
          },
          {
            name: "object_company_id_field",
            label: this.$t("Company/Parent Id"),
            allowed_data_types: ["json"],
            is_required: false,
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_owner_id_field",
            default: "FALL_BACK_TO_COMPANY",
            can_fall_back_to_company: true,
            label: this.$t("Owner Id"),
            allowed_data_types: ["json"],
            is_required: false,
            in_header: this.mappingType === "direct" && this.$route.params.integration_name != "spreadsheet"
          },
          {
            name: "object_co_owners_ids_fields",
            default: ["FALL_BACK_TO_COMPANY"],
            can_fall_back_to_company: true,
            label: this.$t("Co Owners Ids"),
            is_list: true,
            allowed_data_types: ["json"],
            is_required: false,
            in_header: this.mappingType === "direct" && this.$route.params.integration_name != "spreadsheet"
          }
        ],
        contact: [
          {
            name: "object_id_field",
            label: this.$t("Id"),
            allowed_data_types: ["string", "number"],
            in_header: true,
            is_required: true
          },
          {
            name: "object_name_field",
            label: this.$t("Name"),
            allowed_data_types: ["string", "number"],
            in_header: this.mappingType === "direct",
            is_required: this.mappingType === "direct"
          },
          {
            name: "object_company_id_field",
            label: this.$route.params.integration_name == "spreadsheet" ? this.$t("Company Id") : this.$t("Company/Parent Id"),
            allowed_data_types: this.$route.params.integration_name == "spreadsheet" ? ["string", "number"] : ["json"],
            is_required: false,
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_first_name_field",
            label: this.$t("First name"),
            allowed_data_types: ["string"],
            is_required: this.mappingType === "direct",
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_last_name_field",
            label: this.$t("Last name"),
            allowed_data_types: ["string"],
            is_required: this.mappingType === "direct",
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_email_field",
            label: this.$t("Email"),
            allowed_data_types: ["string"],
            is_required: this.mappingType === "direct",
            in_header: this.mappingType === "direct"
          }
        ],
        user: [
          {
            name: "object_id_field",
            label: this.$t("Id"),
            allowed_data_types: ["string", "number"],
            in_header: true,
            is_required: true
          },
          {
            name: "object_name_field",
            label: this.$t("Name"),
            allowed_data_types: ["string", "number"],
            in_header: this.mappingType === "direct",
            is_required: this.mappingType === "direct"
          },
          {
            name: "object_first_name_field",
            label: this.$t("First name"),
            allowed_data_types: ["string"],
            is_required: false,
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_last_name_field",
            label: this.$t("Last name"),
            allowed_data_types: ["string"],
            is_required: false,
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_email_field",
            label: this.$t("Email"),
            allowed_data_types: ["string"],
            is_required: true,
            in_header: this.mappingType === "direct"
          }
        ],
        other: [
          {
            name: "object_id_field",
            label: this.$t("Id"),
            allowed_data_types: ["string", "number"],
            in_header: true,
            is_required: true
          },
          {
            name: "object_name_field",
            label: this.$t("Name"),
            allowed_data_types: ["string", "number"],
            is_required: this.mappingType === "direct",
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_company_id_field",
            label: this.$t("Company/Parent Id"),
            allowed_data_types: ["json", "string", "number"],
            is_required: false,
            in_header: this.mappingType === "direct"
          },
          {
            name: "object_owner_id_field",
            default: "FALL_BACK_TO_COMPANY",
            can_fall_back_to_company: true,
            label: this.$t("Owner Id"),
            allowed_data_types: ["json"],
            is_required: false,
            in_header: this.mappingType === "direct" && this.$route.params.integration_name != "spreadsheet"
          },
          {
            name: "object_co_owners_ids_fields",
            default: ["FALL_BACK_TO_COMPANY"],
            can_fall_back_to_company: true,
            label: this.$t("Co Owners Ids"),
            is_list: true,
            allowed_data_types: ["json"],
            is_required: false,
            in_header: this.mappingType === "direct" && this.$route.params.integration_name != "spreadsheet"
          }
        ]
      },
      integration_name: "",
      loading_fields: false
    }
  },
  computed: {
    is_cross_mapping() {
      return this.mappingType == "cross"
    },
    current_object_header_fields_mappings() {
      let fields = []
      if (this.mappingType == "cross") {
        fields = this.cross_mapping_fields
      } else {
        if (this.mappedObjectType && this.standard_object_types.includes(this.mappedObjectType?.toLowerCase())) {
          fields = this.standard_object_types_mapping_fields[this.mappedObjectType?.toLowerCase()]
        } else {
          fields = this.standard_object_types_mapping_fields["other"]
        }
      }

      return fields.filter((field) => field.in_header)
    }
  },
  watch: {
    object() {
      if (this.object) {
        this.current_object = this.object
        this.getsObjectFields(this.current_object)
      }
    }
  },
  created() {
    this.register_listeners()
    this.current_object = this.object
    this.getsObjectFields(this.object)
    this.integration_name = this.$route.params.integration_name
  },
  activated() {
    this.register_listeners()
  },
  // deactivated() {
  //   this.unregister_listeners()
  // },
  unmounted() {
    this.unregister_listeners()
  },
  mounted() {
    // this.current_object = this.object
    // this.getsObjectFields(this.object)
    // Fill initial custom mapping
    // Note that this.direct_objects_mapping is same as this.mapping.direct_objects only that it is in a different format
    this.mapping?.direct_objects.forEach((obj) => {
      if (!this.direct_objects_mapping[obj.object_name]) {
        this.direct_objects_mapping[obj.object_name] = {
          object_id_field: obj.object_id_field,
          object_website_field: obj.object_website_field,
          object_industry_field: obj.object_industry_field,
          object_company_id_field: obj.object_company_id_field,
          object_parent_id_field: obj.object_parent_id_field,
          object_owner_id_field: obj.object_owner_id_field,
          object_co_owners_ids_fields: obj.object_co_owners_ids_fields,
          object_name_field: obj.object_name_field,
          object_first_name_field: obj.object_first_name_field,
          object_last_name_field: obj.object_last_name_field,
          object_email_field: obj.object_email_field,
          object_name: obj.object_name,
          object_type: obj.object_type,
          mapped_fields: obj.mapped_fields,
          relationship_mapping: obj.relationship_mapping,
          is_parent_required: obj.is_parent_required,
          overwrite_co_owners: obj.overwrite_co_owners
        }

        this.relationship_mapping[obj.object_name] = obj.relationship_mapping
      }
    })
    // Flat the objects from list of {"object_name": "object_name", "object_type": "object_type"} to {"object_name": {"object_type_1": {},"object_type_2":{}} }
    this.mapping?.cross_objects.forEach((obj) => {
      if (!this.cross_objects_mapping[obj.object_name]) {
        this.cross_objects_mapping[obj.object_name] = {}
      }

      if (!this.cross_objects_mapping[obj.object_name][obj.object_type]) {
        this.cross_objects_mapping[obj.object_name][obj.object_type] = {
          object_id_field: obj.object_id_field,
          object_name: obj.object_name,
          object_type: obj.object_type,
          mapped_fields: obj.mapped_fields,
          primary_keys_fields: obj.primary_keys_fields,
          relationship_mapping: obj.relationship_mapping
        }
      }

      this.relationship_mapping[obj.object_name] = obj.relationship_mapping
    })
  },
  methods: {
    isFieldDisabled(field_mapping, source) {
      if (
        (field_mapping.allowed_data_types.includes(source.type) ||
          field_mapping.allowed_data_types.includes(source.data_type.toLowerCase())) &&
        this.mappingType == "cross"
      ) {
        return false
      } else {
        return (
          (this.isFieldMapped(field_mapping.name, source) && field_mapping.is_required) ||
          !(
            (field_mapping.allowed_data_types.includes(source.type) ||
              field_mapping.allowed_data_types.includes(source.data_type.toLowerCase())) &&
            ((source.is_relation &&
              source?.reference_to.includes(field_mapping.allowed_referenced_object) &&
              field_mapping.allowed_referenced_object) ||
              !field_mapping.allowed_referenced_object)
          )
        )
      }
    },

    register_listeners() {
      this.$emitter.on(`${ALL_EMITTERS.START_SYNCING}${this.integration_name.capitalize()}`, () => {
        this.startSyncingBtnClick()
      })
      this.$emitter.on(`${ALL_EMITTERS.SELECT_OTHER_OBJECT}${this.integration_name.capitalize()}`, () => {
        this.validateMapping()
          .then(() => {
            this.$emitter.emit(`${ALL_EMITTERS.OBJECT_CHECKED}${this.integration_name.capitalize()}`, this.object.name)
            this.$emitter.emit(`${ALL_EMITTERS.SELECT_MAPPING_OBJECT}${this.integration_name.capitalize()}`)
          })
          .catch(() => {
            console.debug("mapping issues to fix")
          })
      })

      this.$emitter.on(`${ALL_EMITTERS.FINISH_STEP}${this.integration_name.capitalize()}`, () => {
        this.finishStep()
      })
    },
    unregister_listeners() {
      this.$emitter.off(`${ALL_EMITTERS.START_SYNCING}${this.integration_name.capitalize()}`)
      this.$emitter.off(`${ALL_EMITTERS.SELECT_OTHER_OBJECT}${this.integration_name.capitalize()}`)
      this.$emitter.off(`${ALL_EMITTERS.FINISH_STEP}${this.integration_name.capitalize()}`)
    },
    toggleAllSources() {
      this.toggle_all_is_checked = !this.toggle_all_is_checked
      this.selected_sources = []
      this.object_fields.forEach((field) => {
        this.setIsCheckedSourceStatus(field, this.current_object, this.toggle_all_is_checked)
      })
    },
    getTableFieldsMapping(table_name) {
      // returns the current mapping for the table or the default mapping
      let relationship_mapping = {}

      let mapping = {
        object_name: table_name,
        object_type: this.direct_objects_mapping[table_name]?.object_type || this.mappedObjectType,
        relationship_mapping: this.relationship_mapping[table_name] || relationship_mapping,
        is_parent_required: this.direct_objects_mapping[table_name]?.is_parent_required || false,
        overwrite_co_owners: this.direct_objects_mapping[table_name]?.overwrite_co_owners || false,
        mapped_fields: this.direct_objects_mapping[table_name]?.mapped_fields || [],
        object_id_field:
          this.direct_objects_mapping[table_name]?.object_id_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_id_field")?.default,
        object_name_field:
          this.direct_objects_mapping[table_name]?.object_name_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_name_field")?.default,
        object_owner_id_field:
          this.direct_objects_mapping[table_name]?.object_owner_id_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_owner_id_field")?.default,
        object_website_field:
          this.direct_objects_mapping[table_name]?.object_website_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_website_field")?.default,
        object_industry_field:
          this.direct_objects_mapping[table_name]?.object_industry_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_industry_field")?.default,
        object_parent_id_field:
          this.direct_objects_mapping[table_name]?.object_parent_id_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_parent_id_field")?.default,
        object_company_id_field:
          this.direct_objects_mapping[table_name]?.object_company_id_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_company_id_field")?.default,
        object_co_owners_ids_fields:
          this.direct_objects_mapping[table_name]?.object_co_owners_ids_fields ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_co_owners_ids_fields")?.default ||
          [],
        object_first_name_field:
          this.direct_objects_mapping[table_name]?.object_first_name_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_first_name_field")?.default,
        object_last_name_field:
          this.direct_objects_mapping[table_name]?.object_last_name_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_last_name_field")?.default,
        object_email_field:
          this.direct_objects_mapping[table_name]?.object_email_field ||
          this.standard_object_types_mapping_fields[table_name]?.find((m) => m.name == "object_email_field")?.default
      }

      // NOTE: always set merge relationship_mapping defaults if table Account or User
      // in salesforce as these are defaults for parent child mapping
      if (this.integration_name == "salesforce") {
        if (table_name == "Account") {
          mapping.relationship_mapping = {
            "Account.ParentId": "Account",
            ...mapping.relationship_mapping
          }
        }
        if (table_name == "User") {
          mapping.relationship_mapping = {
            "Account.OwnerId": "Owner",
            ...mapping.relationship_mapping
          }
        }
      } else if (this.integration_name == "hubspot") {
        if (table_name == "companies") {
          mapping.relationship_mapping = {
            "companies.hs_parent_company_id": "hs_parent_company_id",
            ...mapping.relationship_mapping
          }
        }
        if (table_name == "owners") {
          mapping.relationship_mapping = {
            "companies.hubspot_owner_id": "hubspot_owner_id",
            ...mapping.relationship_mapping
          }
        }
      }
      return mapping
    },
    getTableCrossFieldsMapping(table_name, object_type) {
      let relationship_mapping = {}

      let mapping = {
        object_name: table_name,
        object_id_field: this.cross_objects_mapping?.[table_name]?.[object_type]?.object_id_field || null,
        primary_keys_fields: this.cross_objects_mapping?.[table_name]?.[object_type]?.primary_keys_fields || [],
        mapped_fields: this.cross_objects_mapping?.[table_name]?.[object_type]?.mapped_fields || [],
        relationship_mapping: this.relationship_mapping[table_name] || relationship_mapping
      }
      if (this.integration_name == "salesforce") {
        if (table_name == "Account") {
          mapping.relationship_mapping = {
            "Account.ParentId": "Account",
            ...mapping.relationship_mapping
          }
        }
        if (table_name == "User") {
          mapping.relationship_mapping = {
            "Account.OwnerId": "Owner",
            ...mapping.relationship_mapping
          }
        }
      } else if (this.integration_name == "hubspot") {
        if (table_name == "companies") {
          mapping.relationship_mapping = {
            "companies.hs_parent_company_id": "hs_parent_company_id",
            ...mapping.relationship_mapping
          }
        }
        if (table_name == "owners") {
          mapping.relationship_mapping = {
            "companies.hubspot_owner_id": "hubspot_owner_id",
            ...mapping.relationship_mapping
          }
        }
      }
      return mapping
    },

    setEditSourceStatus(source, table, status) {
      source.edit_mode = status
      this.$utils.replaceItem(table.fields, source, "name")
      this.updateField(source, table)
    },
    updateField(source, table) {
      this.$utils.replaceItem(table.fields, source, "name")
      this.$utils.replaceItem(this.objects, table, "name")

      // update the primary key fields, primary keys only on cross
      // update the mapped fields, mapped fields only on cross
      if (this.is_cross_mapping) {
        if (source.is_primary_key) {
          this.$utils.replaceOrAddItem(this.cross_objects_mapping[table.name][this.mappedObjectType]["primary_keys_fields"], source, "name")
        } else {
          this.$utils.removeItem(this.cross_objects_mapping[table.name][this.mappedObjectType]["primary_keys_fields"], source, "name")
        }
        if (source.is_checked) {
          this.$utils.replaceOrAddItem(this.cross_objects_mapping[table.name][this.mappedObjectType]["mapped_fields"], source, "name")
        } else {
          this.$utils.removeItem(this.cross_objects_mapping[table.name][this.mappedObjectType]["mapped_fields"], source, "name")
        }
      } else {
        if (source.is_checked) {
          this.$utils.replaceOrAddItem(this.direct_objects_mapping[table.name]["mapped_fields"], source, "name")
        } else {
          this.$utils.removeItem(this.direct_objects_mapping[table.name]["mapped_fields"], source, "name")
        }
      }
    },
    setIsCheckedSourceStatus(source, table, status) {
      source.is_checked = status
      this.$utils.replaceItem(table.fields, source, "name")

      if (source.is_relation) {
        this.updateReferenceFieldsMapping(source, table, status)
      }
      this.updateField(source, table)
      // replace the table in objects
    },
    updateReferenceFieldsMapping(source, table, status = true) {
      if (source.is_relation) {
        let ref_to_table = source.reference_to[0]
        if (status) {
          if (!this.relationship_mapping[ref_to_table]) {
            this.relationship_mapping[ref_to_table] = {}
          }
          this.relationship_mapping[ref_to_table][table.name + "." + source.name] = source.relationship_name
          let mapped_objects = new Set()
          this.objects.forEach((table) => {
            table.fields.forEach((field) => {
              if (field.is_checked) {
                mapped_objects.add(table.name)
              }
            })
          })
          if (!mapped_objects.has(ref_to_table)) {
            // referenced object not already mapped
            if (this.is_cross_mapping) {
              if (ref_to_table != table.name && !this.cross_objects_mapping[ref_to_table]) {
                this.required_mapping.add(ref_to_table)
              } else {
                this.required_mapping.delete(ref_to_table)
                // eslint-disable-next-line vue/no-undef-properties
                this.cross_objects_mapping[ref_to_table][this.mappedObjectType] = this.getTableCrossFieldsMapping(
                  ref_to_table,
                  this.mappedObjectType
                )
              }
            } else {
              if (ref_to_table != table.name && !this.direct_objects_mapping[ref_to_table]) {
                // add the table if it is not selected as parent id
                if (this.direct_objects_mapping[table.name]?.object_parent_id_field) {
                  this.required_mapping.add(ref_to_table)
                }
              } else {
                this.required_mapping.delete(ref_to_table)
                // eslint-disable-next-line vue/no-undef-properties
                this.direct_objects_mapping[ref_to_table] = this.getTableFieldsMapping(ref_to_table)
              }
            }
          }
        } else {
          delete this.relationship_mapping[ref_to_table]
          this.required_mapping.delete(ref_to_table)
        }
      }
    },

    validateMapping() {
      return new Promise((resolve, reject) => {
        this.required_mapping = new Set()
        this.uploading_errors = new Set()
        for (const [table_name, table_fields_mapping] of Object.entries(this.direct_objects_mapping)) {
          // eslint-disable-next-line vue/no-undef-properties
          if (this.current_object?.name == table_name) {
            this.current_object_header_fields_mappings.forEach((mapping_field) => {
              if (mapping_field.is_required && utils.isEmpty(table_fields_mapping[mapping_field.name])) {
                this.uploading_errors.add(`Finish mapping of object: ${table_name}, Map ${mapping_field.label} field`)
              }
            })
          } else {
            if (utils.isEmpty(table_fields_mapping["object_id_field"])) {
              this.uploading_errors.add(`Finish mapping of object: ${table_name}, Map Id field`)
            }
            if (utils.isEmpty(table_fields_mapping["object_name_field"])) {
              this.uploading_errors.add(`Finish mapping of object: ${table_name}, Map Name field`)
            }
          }
        }
        for (const [table_name, table_fields_mapping] of Object.entries(this.cross_objects_mapping)) {
          for (const [object_type, object_mapping] of Object.entries(table_fields_mapping)) {
            if (utils.isEmpty(object_mapping["object_id_field"])) {
              this.uploading_errors.add(`Finish cross mapping of object: ${table_name} for object type ${object_type}, Map Id field`)
            }
          }
        }

        if (this.uploading_errors.size == 0) {
          if (!this.objects.find((x) => x.name == this.object.name)) {
            this.objects.push(this.current_object)
          }
          resolve(true)
        } else {
          this.$emitter.emit(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
          this.show_uploading_error = true
          reject(false)
        }
      })
    },
    startSyncingBtnClick() {
      if (this.required_mapping.size > 0) {
        this.uploading_errors.add(this.$t("Map the following object(s)") + ` : ${Array.from(this.required_mapping).join(", ")}`)
        this.$emitter.emit(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
        this.show_uploading_error = true
      } else {
        this.validateMapping()
          .then(() => {
            this.startMapping()
          })
          .catch((err) => {
            this.$store.dispatch("ui/hideLoading")
            console.error(err)
          })
      }
    },

    startMapping() {
      this.$store.dispatch("ui/showLoading")
      let data = {}
      this.selected_sources = []

      this.objects.forEach((table) => {
        table.fields.forEach((source) => {
          let source_field = source
          if (source_field.is_checked) {
            this.selected_sources.push(source_field)
          } else {
            this.$utils.replaceItem(table.fields, source_field, "name")
          }
        })
      })
      // remove FALL_BACK_TO_COMPANY in case there are more than one co_owners_ids_fields selected by the user
      for (const [table_name, table_fields_mapping] of Object.entries(this.direct_objects_mapping)) {
        table_fields_mapping["mapped_fields"].forEach((field) => {
          delete field["available_options"]
        })
        if (table_fields_mapping["object_co_owners_ids_fields"]) {
          if (table_fields_mapping["object_co_owners_ids_fields"].length > 1) {
            if (table_fields_mapping["object_co_owners_ids_fields"][0] == "FALL_BACK_TO_COMPANY") {
              this.direct_objects_mapping[table_name]["object_co_owners_ids_fields"] =
                this.direct_objects_mapping[table_name]["object_co_owners_ids_fields"].slice(1)
            }
          }
        }
      }

      data["direct_objects"] = Object.values(this.direct_objects_mapping)

      // first for each cross object, add the object type
      // eslint-disable-next-line no-unused-vars
      for (const [table_name, table_fields_mapping] of Object.entries(this.cross_objects_mapping)) {
        for (const [object_type, object_mapping] of Object.entries(table_fields_mapping)) {
          object_mapping["object_type"] = object_type
          // also the primary keys fields set the object type
          object_mapping["primary_keys_fields"].forEach((field) => {
            field["object_type"] = object_type
            delete field["available_options"]
          })
          object_mapping["mapped_fields"].forEach((field) => {
            delete field["available_options"]
          })
        }
      }

      // flat the cross objects mapping to list
      data["cross_objects"] = Object.values(this.cross_objects_mapping)
        .map((cross_object) => {
          return Object.values(cross_object)
        })
        .flat()

      IntegrationService.updateServiceMapping(this.$route.params.integration_reference, data).then(() => {
        IntegrationService.getServiceOwners(this.$route.params.integration_reference, data)
          .then((result) => {
            this.$store.dispatch("ui/hideLoading")
            this.$emitter.emit(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
            this.service_found_users = result.data
            this.current_object_opened_filter = this.objects[0]
            if (this.service_found_users.length > 0) {
              this.is_send_invitation_emails_modal_open = true
            } else {
              // this.is_filter_records_modal_open = true
              this.submitInvitedUsersAndCreateMetrics()
            }
          })
          .catch(() => {
            this.$store.dispatch("ui/hideLoading")
            this.$emitter.emit(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
            this.service_found_users = []
            this.current_object_opened_filter = this.objects[0]
            this.submitInvitedUsersAndCreateMetrics()
          })
      })
    },
    submitInvitedUsersAndCreateMetrics(service_found_users = []) {
      this.$store.dispatch("ui/showLoading")
      IntegrationService.createServiceMetrics(this.$route.params.integration_reference, {
        included_users: service_found_users
      })
        .then(() => {
          this.$store.dispatch("data/getMetrics")
          this.$store.dispatch("ui/hideLoading")

          this.is_send_invitation_emails_modal_open = false
          this.is_filter_records_modal_open = true
        })
        .catch((err) => {
          this.$toast.open({
            message: this.$t("Error during creating metrics") + ` : ${err.description}`,
            position: "top",
            type: "error",
            duration: 0
          })
          this.$emitter.emit(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
          this.$store.dispatch("data/getMetrics")
          this.$store.dispatch("ui/hideLoading")
          this.is_send_invitation_emails_modal_open = false
        })
    },
    submitFilterAndStartSync(filters) {
      this.$store.dispatch("ui/showLoading")
      IntegrationService.startServiceSync(this.$route.params.integration_reference, {
        new_included_users: this.service_found_users,
        new_mapped_pre_sync_objects_filters: filters
      })
        .then(() => {
          this.finishStep()
          this.$store.dispatch("ui/hideLoading")
          this.$emitter.emit(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
          this.is_filter_records_modal_open = false
        })
        .catch(() => {
          this.$store.dispatch("ui/hideLoading")
          this.$emitter.emit(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
          this.is_filter_records_modal_open = false
        })
    },

    handleCompanyFieldMappingDone(mappingData) {
      // Close the modal
      // // Update the source field with the parent mapping
      this.company_field_mapping_current_field.parent_mapping = mappingData
      this.$utils.replaceItem(this.object.fields, this.company_field_mapping_current_field, "name")
      this.$utils.replaceItem(this.current_object.fields, this.company_field_mapping_current_field, "name")

      // console.log(this.object.fields)
      // console.log(this.current_object.fields)
      // Reset the references
      this.is_company_field_mapping_modal_open = false
      this.company_field_mapping_current_field = null
    },
    getsObjectFields(object) {
      if (!object) {
        return
      }
      let object_name = object.name
      if (object.name == "custom_object") {
        object_name = this.$route.query.obj
      }
      this.$store.dispatch("ui/showLoading", `Loading ${object_name} fields ...`)
      this.loading_fields = true
      IntegrationService.getServiceObject(this.$route.params.integration_reference, {
        object_name: object_name
      })
        .then((response) => {
          this.current_object = response.data

          // eslint-disable-next-line vue/no-undef-properties
          if (this.mappingType == "cross") {
            if (!this.cross_objects_mapping[this.current_object?.name]) {
              this.cross_objects_mapping[this.current_object?.name] = {}
              this.cross_objects_mapping[this.current_object?.name][this.mappedObjectType] = this.getTableCrossFieldsMapping(
                this.current_object.name,
                this.mappedObjectType
              )
            } else {
              if (!this.cross_objects_mapping[this.current_object?.name][this.mappedObjectType]) {
                this.cross_objects_mapping[this.current_object?.name][this.mappedObjectType] = this.getTableCrossFieldsMapping(
                  this.current_object.name,
                  this.mappedObjectType
                )
              }
            }
            // if (!Array.from(Object.keys(this.cross_objects_mapping)).includes(this.current_object?.name)) {
            //   this.cross_objects_mapping[this.current_object.name][this.mappedObjectType] = this.getTableCrossFieldsMapping(
            //     this.current_object.name,
            //     this.mappedObjectType
            //   )
            // } else if (!Array.from(Object.keys(this.cross_objects_mapping[this.current_object.name])).includes(this.mappedObjectType)) {
            //   this.cross_objects_mapping[this.current_object.name][this.mappedObjectType] = this.getTableCrossFieldsMapping(
            //     this.current_object.name,
            //     this.mappedObjectType
            //   )
            // }
          } else {
            if (!Array.from(Object.keys(this.direct_objects_mapping)).includes(this.current_object?.name)) {
              this.direct_objects_mapping[this.current_object.name] = this.getTableFieldsMapping(this.current_object.name)
            }
          }

          this.selected_sources = []
          let checked_list_of_platform_metrics_names = new Set(this.list_of_platform_metrics_names)
          // eslint-disable-next-line vue/no-undef-properties
          this.current_object.fields = this.current_object.fields.map((source) => {
            source["edit_mode"] = false
            this.list_of_options.forEach((option) => {
              let checked = this.isFieldMapped(option, source)
              if (checked) {
                source["is_checked"] = true
              }
            })

            if (source["is_checked"]) {
              this.standard_object_types_mapping_fields[this.current_object.name]?.forEach((field) => {
                let field_name = field.name.replace("_field", "_metric").replace("object_", this.mappedObjectType + "_")

                if (
                  field.name == "object_name_field" &&
                  this.list_of_platform_metrics_names.includes(field_name) &&
                  checked_list_of_platform_metrics_names.has(field_name)
                ) {
                  checked_list_of_platform_metrics_names.delete(field_name)
                  source["default_metric_field_name"] = field_name
                  source["mapped_option"] = this.options_mapper[field.name]
                }
              })

              this.selected_sources.push(source)
            }

            this.$utils.replaceItem(this.object.fields, source, "name")
            this.$utils.replaceItem(this.current_object.fields, source, "name")
            return source
          })
          // replace the objects object
          this.$utils.replaceItem(this.objects, this.current_object, "name")
          this.required_mapping.delete(this.object.name)
          this.$store.dispatch("ui/hideLoading")
          this.loading_fields = false

          this.object_fields = this.current_object.fields.map((object_field) => {
            // find any metric that has the same name as the object_field name
            let mapped_metric = "create"
            let available_options = [{ label: this.$t("Create New Metric"), value: "create" }]
            if (this.mappedObjectType) {
              mapped_metric =
                this.$store.state.metrics.find(
                  (m) =>
                    object_field.data_type == m.data_type &&
                    m.object_type.toLowerCase() == this.mappedObjectType.toLowerCase() &&
                    (m.name.toLowerCase() == object_field.name.toLowerCase() ||
                      m.name.toLowerCase() == object_field.metric_name.toLowerCase() ||
                      m.data_sources?.find(
                        (s) =>
                          (s?.origin_field == object_field.name || s?.origin_field == object_field.metric_name) &&
                          s?.origin_table == this.object.name
                      ))
                ) || "create"
              available_options = this.getObjectTypePossibleMappingOptions(this.mappedObjectType, object_field)
            }
            return {
              ...object_field,
              available_options: available_options,
              object_name: this.current_object.name,
              mapped_object_type: this.mappedObjectType,
              is_primary_key:
                this.cross_objects_mapping[this.current_object?.name]?.[this.mappedObjectType]?.primary_keys_fields.filter(
                  (f) => f.name == object_field.name
                ).length > 0 || false,
              partial_match: "no",
              is_field_mapping_required: this.isFieldMappingRequired(object_field, this.mappedObjectType),
              mapped_metric_reference_name: mapped_metric?.reference_name || null,
              partial_match_regex: "(?<=-)[^-]+(?=-?\\w{7}$)",
              mapped_metric: mapped_metric?.id || "create"
            }
          })
        })
        .catch((er) => {
          console.error(er)
          this.$store.dispatch("ui/hideLoading")
          this.loading_fields = false
        })
      this.objects.push(this.current_object)
    },
    isFieldMappingRequired(object_field, object_type) {
      return object_field && object_type
    },
    getObjectTypePossibleMappingOptions(object_type, object_field) {
      let default_options = [{ label: this.$t("Create New Metric"), value: "create" }]
      if (!object_type) {
        return default_options
      } else {
        return default_options.concat(
          this.$store.state.metrics
            .filter((m) => m.metric_class != "computed_metric" && m.metric_class != "score_metric")
            .filter((m) => m.object_type == this.mappedObjectType)
            .filter(
              (m) =>
                m.data_type == object_field.data_type ||
                this.cross_mapping_fields[0].allowed_data_types.includes(object_field.data_type.toLowerCase())
            )
            .map((m) => ({ ...m, label: m.name, value: m.id }))
        )
      }
    },
    isFieldMapped(name, field) {
      if (this.mappingType == "direct") {
        try {
          if (name == "object_co_owners_ids_fields") {
            return this.direct_objects_mapping[field.table_name][name].includes(field.name)
          } else {
            return this.direct_objects_mapping[field.table_name][name] == field.name
          }
        } catch (_) {
          return false
        }
      } else {
        return this.cross_objects_mapping[field.table_name][this.mappedObjectType][name] == field.name
      }
    },
    // isOptionFixed(name,table_name) {
    //     return this.getTableFieldsMapping(table_name)[name]?.in_header == true
    // },
    radioBtnClicked(mapping_field, source, table) {
      /* Performs
      1- Adds the field to the direct_objects_mapping

       */
      let table_name = table.name
      let column_field_name = mapping_field.name

      if (!this.isFieldMapped(column_field_name, source)) {
        source.is_checked = true
        source["mapped_option"] = this.options_mapper[column_field_name] || null
      } else {
        source.is_checked = !source.is_checked
        source["mapped_option"] = null
      }
      // if the option is object_company_id_field and the integration is spreadsheet we set the object_type to company
      if (column_field_name == "object_company_id_field" && this.integration_name == "spreadsheet") {
        source["object_type"] = "company"
        // source["required_actions"] = []
      }
      this.$utils.replaceItem(table.fields, source, "name")
      if (this.mappingType == "direct") {
        if (!this.direct_objects_mapping[table_name]) {
          this.direct_objects_mapping[table_name] = {
            object_id_field: "Id",
            object_name: table_name,
            object_owner_id_field: null,
            object_co_owners_ids_fields: [],
            relationship_mapping: {},
            is_parent_required: false,
            overwrite_co_owners: false
          }
        }
        // If the field is a co_owners_ids field
        if (column_field_name == "object_co_owners_ids_fields") {
          utils.toggleItemFromList(this.direct_objects_mapping[table_name]["object_co_owners_ids_fields"], source.name)
        } else {
          if (source.is_checked) {
            this.direct_objects_mapping[table_name][column_field_name] = source.name
          } else {
            this.direct_objects_mapping[table_name][column_field_name] = null
          }
        }
        // Show the modal to select the integration and table field for mapping only when the field is not relation "foreign key"
        if (column_field_name == "object_company_id_field" && !source.is_relation) {
          this.company_field_mapping_current_field = source
          if (source.is_checked) {
            if (!this.company_field_mapping_current_field["parent_mapping"]) {
              this.company_field_mapping_current_field["parent_mapping"] = {
                object_type: this.mappedObjectType,
                metric_id: null
              }
            }

            this.is_company_field_mapping_modal_open = true
          } else {
            this.company_field_mapping_current_field["parent_mapping"] = null
          }
        }
      } else {
        if (!this.cross_objects_mapping[table_name]) {
          this.cross_objects_mapping[table_name] = {}
          this.cross_objects_mapping[table_name][this.mappedObjectType] = {
            object_name: table_name,
            object_id_field: null,
            object_type: this.mappedObjectType,
            primary_keys_fields: [],
            relationship_mapping: {}
          }
        } else {
          if (!this.cross_objects_mapping[table_name][this.mappedObjectType]) {
            this.cross_objects_mapping[table_name][this.mappedObjectType] = {
              object_name: table_name,
              object_id_field: null,
              object_type: this.mappedObjectType,
              primary_keys_fields: [],
              relationship_mapping: {}
            }
          }
        }
        if (source.is_checked) {
          this.cross_objects_mapping[table_name][this.mappedObjectType][column_field_name] = source.name
        } else {
          this.cross_objects_mapping[table_name][this.mappedObjectType][column_field_name] = null
        }
      }

      // this.direct_objects_mapping[table.name]["relationship_mapping"] = this.relationship_mapping.has(table.name) ? this.relationship_mapping[table.name] : {}

      this.setIsCheckedSourceStatus(source, this.current_object, source.is_checked)
    },
    finishStep() {
      this.$emitter.emit(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
      // refresh the user to remove the onboarding msg
      this.$store.dispatch("auth/refreshMe")
      // refresh data
      this.$store.dispatch("getPortfolio")
      this.$store.dispatch("data/getMetrics")
      this.$store.dispatch("data/getUsers")
      this.$router.push("/admin/integrations")
    }
  }
}
</script>
